import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_BANKRECS,
  RECEIVE_BANKREC_METADATA,
  RECEIVE_BANKREC_FAILURE,
  RECEIVING_BANKREC,
  SAVING_BANKREC,
  SAVING_BANKREC_FAILURE,
  UPDATING_BANKREC,
  UPDATING_BANKREC_FAILURE,
  DELETE_BANKREC,
  DELETING_BANKREC,
  DELETING_BANKREC_FAILURE,
  INCREMENT_BANKREC_COUNT,
  RECEIVE_BANKREC_OPTIONS,
  RECEIVING_BANKREC_OPTIONS,
  RECEIVE_BANKREC_OPTIONS_FAILURE,
  CLEAR_BANKREC_STATE,
  RESET_BANKREC_META,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { mapPostOptionsToFields } from '@/utils/helpers/MapPostOptionsToFields'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const bankRecSchema = new schema.Entity('bankRecs')
const bankRecListSchema = new schema.Array(bankRecSchema)

const state = {
  data: {},
  cache: {},
  reconcilableData: {},
  reconcilableCache: {},
  receivingReconcilables: false,
  reconcilableCount: 0,
  reconcilableShown: 0,
  options: {},
  receivingOptions: false,
  receiveOptionsFailure: null,
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false
}

const actions = {
  fetchBankRecsWithQuery (store, { query = null, cacheData = true } = {}) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_BANKREC, true)
    this.commit(RESET_BANKREC_META)
    return http.get(buildUrlWithQuery('bankrec', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKREC, false)
        if (!r.hasOwnProperty('results')) return Promise.reject('Error fetching bankrecs')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, bankRecListSchema)
        if (cacheData) {
          this.commit(RECEIVE_BANKRECS, { data: entities.bankRecs })
          this.commit(RECEIVE_BANKREC_METADATA, { next, previous, count, result: result.length })
        }
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_BANKREC, false)
        this.commit(RECEIVE_BANKREC_FAILURE, handleXHRError(e))
      })
  },
  fetchBankRecsByPage (store, page) {
    const { cache } = store.state
    const instanceUrl = this.getters.getActiveInstance.url
    let url = buildUrlWithQuery('bankrec', { page }, instanceUrl)
    let next = (page + 1) > Math.ceil(state.count / 20)
      ? null
      : buildUrlWithQuery('bankrec', { page: page + 1 }, instanceUrl)
    let previous = (page - 1) <= 0
      ? null
      : buildUrlWithQuery('bankrec', { page: page - 1 }, instanceUrl)
    // Check if data is already cached
    const c = Object.values(cache).filter(v => v.hasOwnProperty('page') && parseInt(v.page) === parseInt(page))
    if (c && c.length && c.length <= 20) {
      const e = normalize(c, bankRecListSchema)
      this.commit(RECEIVE_BANKRECS, { page, data: e.entities.bankRecs })
      this.commit(RECEIVE_BANKREC_METADATA, { next, previous, result: e.result.length })
      return e
    }
    // If not, fetch
    this.commit(RECEIVING_BANKREC, true)
    return http.get(url, { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKREC, false)
        if (!r) return Promise.reject('Error fetching bankrecs')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, bankRecListSchema)
        if (!result || !Array.isArray(result)) return Promise.reject('Error normalizing response')
        this.commit(RECEIVE_BANKRECS, { page, data: entities.bankRecs })
        this.commit(RECEIVE_BANKREC_METADATA, { next, previous, count, result: result.length })
        return entities
      })
      .catch(e => {
        this.commit(RECEIVING_BANKREC, false)
        this.commit(RECEIVE_BANKREC_FAILURE, handleXHRError(e))
      })
  },
  fetchBankRecOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_BANKREC_OPTIONS, true)
    const headers = getHeaders(this.getters.getToken)
    return http({ url: buildUrl('bankrec', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKREC_OPTIONS, false)
        if (!r) Promise.reject('Error fetching bankrec options')
        this.commit(RECEIVE_BANKREC_OPTIONS, r)
        return r
      })
      .catch(e => {
        this.commit(RECEIVING_BANKREC_OPTIONS, false)
        this.commit(RECEIVE_BANKREC_OPTIONS_FAILURE, handleXHRError(e))
      })
  },
  fetchReconcilables (store, payload) {
    const { type, query } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return http.get(buildUrlWithQuery(`${type}/reconcilable`, query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        return {
          type,
          count: r.length,
          entities: r.map(v => {
            if (v) v.type = type.split('/')[1]
            return v
          }),
          recon: true
        }
      })
      .catch(e => {
        return e
      })
  },
  saveBankRec (store, payload) {
    if (!payload) return this.commit(SAVING_BANKREC_FAILURE, 'Error saving bankrec')
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(SAVING_BANKREC, true)
    this.commit(SAVING_BANKREC_FAILURE, null)
    return http.post(buildUrl('bankrec', null, instanceUrl), payload, { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(SAVING_BANKREC, false)
        if (!r) return Promise.reject('Error saving bankrec')
        const { entities, result } = normalize(r, bankRecSchema)
        if (!result || (Array.isArray(result) && !result.length)) return Promise.reject('Error normalizing response')
        this.commit(RECEIVE_BANKRECS, { data: entities.bankRecs })
        this.commit(INCREMENT_BANKREC_COUNT, 1)
        return r
      })
      .catch(e => {
        this.commit(SAVING_BANKREC, false)
        this.commit(SAVING_BANKREC_FAILURE, handleXHRError(e))
      })
  },
  updateBankRec (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_BANKREC, id)
    this.commit(UPDATING_BANKREC_FAILURE, { id: '', err: '' })
    return http.patch(buildUrl(`bankrec/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_BANKREC, null)
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, bankRecSchema)
          if (!result || (Array.isArray(result) && !result.length)) return Promise.reject('Error normalizing response')
          this.commit(RECEIVE_BANKRECS, { data: entities.bankRecs })
          return r.body
        } else {
          return Promise.reject(r)
        }
      })
      .catch(e => {
        this.commit(UPDATING_BANKREC, null)
        this.commit(UPDATING_BANKREC_FAILURE, { id, err: handleXHRError(e, 'Failed to update bankrec') })
      })
  },
  clearBankRecState (store, clearCache = false) {
    this.commit(CLEAR_BANKREC_STATE, clearCache)
  },
  resetBankRecMeta () {
    return this.commit(RESET_BANKREC_META)
  }
}

const getters = {
  getBankRecs: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllBankRecs: (state) => {
    return state.cache ? Object.values(state.cache).map(v => ({ ...v })) : []
  },
  getBankRecMetadata: (state) => {
    const { shown, count, previous, next } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, previous, next }
  },
  getBankrecFetchStatus: (state) => {
    return state.receivingData
  },
  getBankRecById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getBankRecSaveStatus: (state) => {
    return state.saving
  },
  getBankRecSaveFailure: (state) => {
    return state.saveFailure
  },
  getBankRecUpdateStatus: (state) => {
    return state.updating
  },
  getBankRecUpdateFailure: (state) => {
    return state.updateFailure
  },
  getCurrentBankRecPage: (state) => {
    if (state.currentPage) return state.currentPage
  },
  getBankRecOptions: (state) => {
    return state.options ? state.options : {}
  },
  getBankRecFields: (state) => {
    if (state.options && state.options.actions && state.options.actions['POST']) {
      return mapPostOptionsToFields(state.options.actions['POST'])
    }
  }
}

const mutations = {
  [RECEIVE_BANKRECS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_BANKREC] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_BANKREC_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_BANKREC] (state, payload) {
    state.saving = payload
  },
  [SAVING_BANKREC_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_BANKREC] (state, payload) {
    state.updating = payload
  },
  [UPDATING_BANKREC_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_BANKREC] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_BANKREC] (state, payload) {
    state.deleting = payload
  },
  [DELETING_BANKREC_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { status: false, id, err }
  },
  [INCREMENT_BANKREC_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVING_BANKREC_OPTIONS] (state, payload) {
    state.receivingOptions = payload
  },
  [RECEIVE_BANKREC_OPTIONS] (state, payload) {
    state.options = Object.assign({}, payload)
  },
  [RECEIVE_BANKREC_OPTIONS_FAILURE] (state, payload) {
    state.receiveOptionsFailure = payload
  },
  [RECEIVE_BANKREC_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_BANKREC_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_BANKREC_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
